<template>
  <ResponsiveContainer
    default-breakpoint='tiny'
    :breakpoints='{ small: 480, medium: 680, large: 900 }'
    v-slot='container'
  >
    <div
      class='blister-finder'
      :class='[
        `size-${container.breakpoint} size-min-${container.breakpoint}`,
        ...container.smaller.map(name => `size-min-${name}`)
      ]'
    >

<!--      <select v-model='$i18n.locale'>-->
<!--        <option v-for="(lang, i) in ['de', 'en', 'fr']" :key='`Lang${i}`' :value='lang'-->
<!--                :selected="$i18n.locale === lang ? 'selected': false">-->
<!--          {{ lang.toUpperCase() }}-->
<!--        </option>-->
<!--      </select>-->

      <div class='layout'>
        <div>
          <h2 class='blister-headline'>{{$t('productName')}}</h2>
          <h3>{{ $t('filter.productDimensions') }}</h3>
          <div class='filter'>
            <label
              for='input-width'
              class='blister-input-label blister-input-width-label'>
              {{ $t('filter.width') }}
              <Input
                type='number'
                step='0.1'
                min='0.1'
                autocomplete='off'
                id='input-width'
                class='blister-input blister-input-width'
                v-model.number='width'
              />
            </label>


            <X class='x x-1' />

            <label
              for='input-length'
              class='blister-input-label blister-input-length-label'>
              {{ $t('filter.length') }}
              <Input
                type='number'
                step='0.1'
                min='0.1'
                autocomplete='off'
                id='input-length'
                class='blister-input blister-input-length'
                v-model.number='length'
              />
            </label>


            <X class='x x-2' />

            <label
              for='input-height'
              class='blister-input-label blister-input-height-label'>
              {{ $t('filter.height') }}
              <Input
                type='number'
                step='0.1'
                min='0.1'
                autocomplete='off'
                id='input-height'
                class='blister-input blister-input-height'
                v-model.number='height'
              />
            </label>

          </div>

          <ResultsTable
            class='blister-footer'
            v-if='hasResults'
            :matched-blisters='restrictedMatches'
            @blister-selected='blisterSelected'
          />
          <Alert v-else-if='hasSufficientInput' class='no-results-alert'>{{ $t('results.noResults') }}</Alert>
        </div>
        <ThreeVisual :blister='selectedBlister' :placeholder-dimensions='{width, height, depth: length}' />
      </div>


    </div>
  </ResponsiveContainer>
</template>

<script>
import { search, results } from './search'
import blisters from './blisters'
import ResponsiveContainer from './components/ResponsiveContainer'
import ResultsTable from './components/ResultsTable'
import Input from './components/Input'
import X from './components/X'
import ThreeVisual from './components/ThreeVisual'
import Alert from './components/Alert'

export default {
  components: { ThreeVisual, ResponsiveContainer, ResultsTable, Input, X, Alert },
  data: () => ({
    blisters,
    height: null,
    width: null,
    length: null,
    selectedBlister: null
  }),
  created () {
    if (window.lang) {
      this.$i18n.locale = window.lang.toLowerCase()
    }
  },
  methods: {
    search () {
      search(this.width, this.length, this.height)
    },
    isHighlighted (id) {
      return id in this.highlightedBlisters
    },
    blisterSelected (blister) {
      this.selectedBlister = blister
    }
  },
  computed: {
    richBlisters: vm =>
      vm.blisters.map(blister => ({
        ...blister,
        area: blister.width * blister.length
      })),
    hasWidth: vm => typeof vm.width === 'number',
    hasLength: vm => typeof vm.length === 'number',
    hasHeight: vm => typeof vm.height === 'number',
    hasSufficientInput: vm => vm.hasWidth && vm.hasLength,
    area: vm => (vm.hasWidth && vm.hasLength ? vm.width * vm.length : 0),
    matches: () => results.matches,
    restrictedMatches: () => results.restrictedMatches,
    highlightedNames () {
      return this.restrictedMatches.map(match => match.name)
    },
    hasResults: vm => vm.matches.length > 0,
    maxBlisterWidth: vm =>
      vm.blisters.reduce((carry, blister) => Math.max(carry, blister.width), 0),
    maxBlisterLength: vm =>
      vm.blisters.reduce((carry, blister) => Math.max(carry, blister.length), 0)
  },
  watch: {
    width: 'search',
    length: 'search',
    height: 'search'
  }

}
</script>

<style lang='scss' scoped>
.blister-finder {
  --strong-accent: #286fbb;
  --medium-accent: #5993d1;
  --weak-accent: #93b7dc;

  padding: 20px;

  @at-root .size-min-medium {
    padding: 40px;
  }

  color: #fff;
  background-color: var(--strong-accent);
}

.filter {
  display: flex;
  align-items: center;
  gap: 1em;
}

.blister-header {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(7, auto);
  grid-template-areas: 'headline' 'width-label' 'width-input' 'length-label' 'length-input' 'height-label' 'height-input';
  align-items: center;
  grid-gap: 3px 10px;
  margin-bottom: 40px;

  @at-root .size-min-small & {
    grid-template-columns: 1fr auto 1fr auto 1fr;
    grid-template-rows: repeat(3, auto);
    grid-template-areas: 'headline headline headline headline headline' 'width-label . length-label . height-label' 'width-input x-1 length-input x-2 height-input';
    grid-gap: 3px 10px;
  }

  @at-root .size-min-medium & {
    grid-template-columns: 1fr repeat(5, auto);
    grid-template-rows: repeat(2, auto);
    grid-template-areas: '. width-label . length-label . height-label' 'headline width-input x-1 length-input x-2 height-input';
    grid-gap: 3px 10px;
  }
}

.blister-footer {
  margin-top: 40px;
}

.blister-headline {
  font-size: 30px;
  font-weight: inherit;
  margin: 0;
  grid-area: headline;

  @at-root .size-min-small & {
    margin-bottom: 15px;
  }

  @at-root .size-min-medium & {
    margin-bottom: 0;
  }
}

.x {
  margin: auto;
  display: none;

  @media (min-width: 1015px) {
    display: block;
  }
}

.blister-input-label {
  margin-top: 10px;

  @at-root .size-min-small & {
    margin-top: 0;
  }
}

.blister-svg {
  width: 100%;
  height: auto;
}

.blister-input-width-label {
  grid-area: width-label;
}

.blister-input-width {
  grid-area: width-input;
}

.x-1 {
  grid-area: x-1;
}

.size-min-medium .blister-input {
  width: 15ch;
}

.blister-input-length-label {
  grid-area: length-label;
}

.blister-input-length {
  grid-area: length-input;
}

.x-2 {
  grid-area: x-2;
}

.blister-input-height-label {
  grid-area: height-label;
}

.blister-input-height {
  grid-area: height-input;
}

.layout {
  display: grid;
  grid-template-columns: 100%;
  grid-gap: 40px;

  @media(min-width: 900px) {
    grid-template-columns: calc(50% - 20px) calc(50% - 20px);
  }
}

.no-results-alert {
  margin-top: 1.5em;
}
</style>
